export function interpolateY(logs, value) {
    for (let i = 0; i < logs.length; i++) {
        if (Math.abs(logs[i].timestamp - value) < 1e-6) return logs[i]

        if (logs[i].timestamp > value) {
            if (i === 0) return logs[i]

            const diffLess = Math.abs(logs[i - 1].timestamp - value)
            const diffMore = Math.abs(logs[i].timestamp - value)
            const smallest = diffLess < diffMore ? 'less' : 'more'
            const ratio = smallest === 'less' ? diffMore / diffLess : diffLess / diffMore

            return {timestamp: value, log: logs[i].log.map((v, idx) => (smallest === 'less' ? logs[i - 1].log[idx] : v) * ratio / (ratio + 1) + (smallest === 'less' ? v : logs[i - 1].log[idx]) * 1 / (ratio + 1))}
        }
    }

    return logs[logs.length - 1]
}

export function interpolateX(log: number[], index: number): number {
    if (Math.abs(index - Math.round(index)) < 1e-6) return log[index]

    if (index > log.length - 1) return log[log.length - 1]

    const diffLess = Math.abs(Math.floor(index) - index)
    const diffMore = Math.abs(Math.ceil(index) - index)
    const smallest = diffLess < diffMore ? 'less' : 'more'
    const ratio = smallest === 'less' ? diffMore / diffLess : diffLess / diffMore

    return log[smallest === 'less' ? Math.floor(index) : Math.ceil(index)] * ratio / (ratio + 1) + log[smallest === 'less' ? Math.ceil(index) : Math.floor(index)] * 1 / (ratio + 1)
}

export function getProportionalY(logs: { timestamp: number; log: number[] }[], initialY: number): number {
    for (let i = 0; i < logs.length; i++) {
        if (Math.abs(logs[i].timestamp - initialY) < 1e-6) return i

        if (logs[i].timestamp > initialY) {
            if (i === 0) return 0

            const diffLess = Math.abs(logs[i - 1].timestamp - initialY)
            const diffMore = Math.abs(logs[i].timestamp - initialY)
            const smallest = diffLess < diffMore ? 'less' : 'more'
            const ratio = smallest === 'less' ? diffMore / diffLess : diffLess / diffMore

            return (
                ((smallest === 'less' ? i - 1 : i) * ratio) / (ratio + 1) +
                (smallest === 'less' ? i : i - 1) / (ratio + 1)
            )
        }
    }

    return logs.length - 1
}

function separableInterpolation(
    image: number[][],
    x0: number,
    y0: number,
    kernel: (x: number) => number,
    order: number
): number {
    let q = 0
    for (let j = 0; j < 2 * order; j++) {
        const v = Math.floor(y0) + j - order + 1
        let p = 0

        for (let i = 0; i < 2 * order; i++) {
            const u = Math.floor(x0) + i - order + 1

            if (v < 0 || !image[v]) continue

            const length = image[v].length
            p = p + image[v][u < 0 ? length + u : u % length] * kernel(x0 - u)
        }

        q = q + p * kernel(y0 - v)
    }

    return q
}

function lanczosKernel(order: number): (x: number) => number {
    return (x): number => {
        if (Math.abs(x) < 1e-6) return 1
        if (Math.abs(x) >= order) return 0

        return order * ((Math.sin(Math.PI * (x / order)) * Math.sin(Math.PI * x)) / (Math.PI * Math.PI * x * x))
    }
}

function cubicSplineKernel(a: number, b: number): (x: number) => number {
    return (x): number => {
        const absX = Math.abs(x)

        if (absX >= 2) return 0
        if (absX < 1) {
            return ((-6 * a - 9 * b + 12) * absX * absX * absX + (6 * a + 12 * b - 18) * absX * absX - 2 * b + 6) / 6
        }

        return ((-6 * a - b) * absX * absX * absX + (30 * a + 6 * b) * absX * absX + (-48 * a - 12 * b) * absX + 24 * a + 8 * b) / 6
    }
}

function cubicKernel(a: number): (x: number) => number {
    return (x): number => cubicSplineKernel(a, 0)(x)
}

function catmullRomKernel(x: number): number {
    return cubicSplineKernel(0.5, 0)(x)
}

function cubicBSplineKernel(x: number): number {
    return cubicSplineKernel(0, 1)(x)
}

function mitchellNetravaliKernel(x: number): number {
    return cubicSplineKernel(1 / 3, 1 / 3)(x)
}

export enum Interpolation {
    Lanczos,
    Cubic,
    CatmullRom,
    BSpline,
    MitchellNetravali
}

export function interpolate(method: Interpolation, image: number[][], x0: number, y0: number): number {
    let kernel: (x: number) => number
    let order: number

    switch (method) {
        case Interpolation.Lanczos:
            kernel = lanczosKernel(3)
            order = 3
            break
        case Interpolation.Cubic:
            kernel = cubicKernel(1)
            order = 2
            break
        case Interpolation.CatmullRom:
            kernel = catmullRomKernel
            order = 2
            break
        case Interpolation.BSpline:
            kernel = cubicBSplineKernel
            order = 2
            break
        case Interpolation.MitchellNetravali:
            kernel = mitchellNetravaliKernel
            order = 2
            break
        default:
            throw new Error(`Unknown interpolation method: ${method}`)
    }

    return separableInterpolation(image, x0, y0, kernel, order)
}
